import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { buildUser } from '@/lib/utils/buildUser'
import { useAuth } from '@/lib/utils/useAuth'
import { useLocalStorage } from '@/lib/utils/useLocalStorage'
import { FaroEvents, emitEvent } from '@/lib/observability/events'
import router from '@/router'
import type { AppUser, ImpersonationState } from '@/lib/types'

const THEME_STORAGE_KEY = 'lockerserver:theme-mode'
const DARK_CLASS = 'app-dark'

type ThemeMode = 'light' | 'dark' | 'system'

/**
 * Global app state. Holds the authenticated user wrapper, the
 * app-loading flag (drives the splash overlay), and theme mode.
 *
 * The user object stays a `ref<AppUser>` (not `reactive`) so that
 * re-assignment (login / logout) replaces the entire wrapper —
 * `globalStore.user = buildUser(newUser)` works clean.
 *
 * Theme is persisted to localStorage; the system-preference watcher
 * fires only when `themeMode === 'system'`.
 *
 * ## Auth boot — `ready` Promise
 *
 * The session cookie is the persistent layer. In-memory state on
 * the FE is just a derived view of it. The boot probe — one
 * `GET /api/auth/me` per app lifecycle — materializes the view
 * from the cookie. This store OWNS that probe: the first call to
 * `useGlobalStore()` runs setup, which kicks off the probe and
 * caches its Promise on `ready`. Anything that needs a definite
 * auth state (the route guard) `await`s `store.ready`.
 *
 * Subsequent state transitions happen in-process — no re-probe:
 *   - Login response writes the user (`attemptLogin` in useAuth)
 *   - Logout clears it (`attemptLogout`)
 *   - 401 from any API call clears it (`useAPI`'s catch handler);
 *     the watcher below catches that null transition and routes
 *     to Login.
 *
 * The Promise resolves regardless of probe outcome — failure means
 * "anonymous", which is just one of two valid resolved states.
 */
export const useGlobalStore = defineStore('global', () => {
  const user = ref<AppUser>(buildUser())
  const appLoading = ref<boolean>(true)
  /**
   * Active USER impersonation, if any - a platform super_admin viewing the
   * app as another user. Hydrated from the server (never inferred locally)
   * so a hard refresh still shows the banner and the way back; `useAuth`
   * owns the fetch. Distinct from the operating-tenant picker, which keeps
   * your own identity.
   */
  const impersonation = ref<ImpersonationState>({
    active: false,
    started_at: null,
    impersonator: null,
  })
  // Persisted theme — composable round-trips the string verbatim, so
  // existing `'light'`/`'dark'`/`'system'` values in users' storage
  // are read back as-is. Default `'dark'` matches the canonical
  // first-visit landing (see `initTheme`).
  const themeMode = useLocalStorage<ThemeMode>(THEME_STORAGE_KEY, 'dark')
  let systemMediaQuery: MediaQueryList | undefined
  let systemListener: ((e: MediaQueryListEvent) => void) | undefined

  /**
   * Clear the auth state — set the user wrapper to its anonymous
   * shell. The single seam for "this user is no longer authed",
   * used by:
   *   - `attemptLogout` in `useAuth`
   *   - the route guard's cookie-canary check (XSRF-TOKEN gone →
   *     session expired client-side)
   *   - the HTTP client's 401 handler (via `globalStore.user =
   *     buildUser()` today — to be migrated to this method when
   *     useAPI is next touched)
   *
   * Hides the `buildUser()` call so call sites read the operation
   * (clearAuth), not its implementation (re-constructing the empty
   * shell). Triggering the watcher below is intentional — it
   * cancels in-flight requests + routes to Login.
   */
  const clearAuth = (): void => {
    user.value = buildUser()
    impersonation.value = { active: false, started_at: null, impersonator: null }
    emitEvent(FaroEvents.AUTH_SESSION_CLEARED)
  }

  // Boot probe. Kicked off the moment the store is first accessed.
  //
  // The `Promise.resolve().then(...)` deferral is load-bearing:
  // Pinia setup-style stores register their proxy BEFORE running
  // setup and only `Object.assign` the setup return value AFTER
  // setup completes. `useAuth()` internally calls
  // `useGlobalStore()` (recursive), and the recursive call returns
  // the same proxy — but DURING setup that proxy hasn't received
  // any state yet, so `globalStore.user` would be `undefined`
  // (read inside `checkAuth`) and throw. Microtask-deferring the
  // first probe runs `checkAuth`'s body AFTER setup has returned
  // and the state is wired.
  //
  // `useAuth().checkAuth(false)` always resolves (catches
  // internally), so this is safe as a plain `Promise<void>`.
  // `shouldRedirect=false` keeps a 401 from mutating state via
  // useAPI's catch handler — the probe result here IS the state.
  const ready: Promise<void> = Promise.resolve().then(async () => {
    await useAuth().checkAuth(false)
  })

  /** Effective theme — resolves `'system'` to the OS preference. */
  const currentTheme = computed<'light' | 'dark'>(() => {
    if (themeMode.value !== 'system') return themeMode.value
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  /**
   * Toggle the `app-dark` class on `<html>` based on `currentTheme`.
   *
   * Wraps the flip in a `theme-flipping` class on `<html>` that
   * suppresses every transition app-wide for one frame (see the
   * `html.theme-flipping *` rule in `main.scss`). Without this,
   * thousands of elements run their universal background/color
   * transition simultaneously when the `--theme-*` vars change —
   * a multi-second paint storm felt as severe lag on content-dense
   * pages. The double-rAF (frame-after-paint) is the canonical
   * "let the browser commit the class write before removing it"
   * idiom; a single rAF can race with the same-task class flip.
   */
  const applyTheme = (): void => {
    const root = document.documentElement
    root.classList.add('theme-flipping')
    if (currentTheme.value === 'dark') root.classList.add(DARK_CLASS)
    else root.classList.remove(DARK_CLASS)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove('theme-flipping')
      })
    })
  }

  const setTheme = (mode: ThemeMode): void => {
    themeMode.value = mode
    // Persistence handled by `useLocalStorage`'s watcher above.
    applyTheme()
  }

  const initTheme = (): void => {
    // `themeMode` already initialized from localStorage by
    // `useLocalStorage` (default `'dark'` — matches `_tokens.scss`'s
    // canonical primary mode for first-visit users). Just apply.
    applyTheme()
  }

  /**
   * Listens to OS theme changes. Only matters when `themeMode` is
   * `'system'`; in `'light'`/`'dark'` mode the user opted out.
   */
  const watchSystemTheme = (): void => {
    if (systemMediaQuery && systemListener) {
      systemMediaQuery.removeEventListener('change', systemListener)
    }
    systemMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemListener = () => {
      if (themeMode.value === 'system') applyTheme()
    }
    systemMediaQuery.addEventListener('change', systemListener)
  }

  /**
   * Auth-state lockout. When `user.detail` flips to null (logout,
   * 401 surfaced by the HTTP client, session expiry, manual clear),
   * the entire app reacts to that state change — NOT to the call
   * site that triggered it. This keeps every triggering path
   * (logout button, axios 401 handler, idle-timeout future feature)
   * funneling through the same exit:
   *
   *   1. Cancel every in-flight HTTP request so a stale 200 can't
   *      land in a store AFTER the lockout has begun.
   *   2. Navigate to Login via the router (SPA flow). If we're
   *      already on /login, no-op — the navigation guard would
   *      bounce us, and a duplicate push is noisy.
   *
   * The HTTP client (`useAPI.ts`) is wired to call
   * `BackendApi.cancelAllInFlight()` lazily via the late-bound
   * import below — keeps this store free of an HTTP dependency at
   * module init.
   */
  watch(
    () => user.value.detail,
    async (current, previous) => {
      // Only the null transition matters (login → logout, or session
      // expiry detected mid-session). Initial `null` on first paint
      // is handled by the route guard, not this watcher.
      if (current !== null || previous === undefined) return
      if (appLoading.value) return

      // Cancel everything in flight. Late-bound import avoids the
      // store ↔ HTTP-client circular at module init.
      const { useAPI } = await import('@/lib/utils/useAPI')
      useAPI().cancelAllInFlight('auth-lockout')

      if (router.currentRoute.value.name !== 'Login') {
        await router.push({ name: 'Login' })
      }
    },
  )

  return {
    user,
    appLoading,
    impersonation,
    ready,
    themeMode,
    currentTheme,
    clearAuth,
    setTheme,
    initTheme,
    watchSystemTheme,
  }
})
